import { ArrowLeftRight, Box, ChevronDown, ChevronUp, Info, Plus, Trash2 } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Tab } from '@headlessui/react';

import { Button } from '@/components/Common/button';
import { Card, CardContent, CardHeader } from '@/components/Common/Card';
import yaml from 'js-yaml';

/**
 * Represents an entity type in the ontology schema.
 * Example: Logiciel, Machine, Notaire, ActeNotarie, etc.
 */
interface EntityType {
  id: string;                    // Unique ID for React rendering (not saved to YAML)
  name: string;                  // Entity type name (ex: "Logiciel", "Notaire")
  parent: string | null;         // Parent entity type (ex: "ActeurJuridique")
  description: string;           // Descriptive text for LLM guidance
  synonymes: string[];           // Alternative names (ex: ["officier public", "tabellion"])
  proprietes: string[];          // Properties to extract (ex: ["nom", "version"])
  examples: string[];            // Example values (ex: ["Maître Dupont", "GO2cam V6"])
}

/**
 * Represents a relation type in the ontology schema.
 * Example: utilise, redige, estRegiPar, etc.
 */
interface RelationType {
  id: string;                    // Unique ID for React rendering (not saved to YAML)
  name: string;                  // Relation name (ex: "utilise", "redige")
  description: string;           // Descriptive text for LLM guidance
  source: string;                // Source entity type name
  cible: string;                 // Target entity type name
}

/**
 * Structure of ontology_schema.yml file.
 * Parsed from YAML and converted back to YAML on save.
 */
interface OntologySchemaData {
  description?: string;          // Global schema description
  entity_types?: Record<string, Omit<EntityType, 'name' | 'id'>>;
  relation_types?: Record<string, Omit<RelationType, 'name' | 'id'>>;
}

/**
 * Props for OntologySchemaForm component.
 */
interface OntologySchemaFormProps {
  initialContent?: string;       // YAML content to parse and display
  onSave: (yamlContent: string) => Promise<void>;  // Callback with YAML string
  onCancel: () => void;          // Close modal without saving
}

export function OntologySchemaForm({ initialContent, onSave, onCancel }: OntologySchemaFormProps) {
  const { t } = useTranslation();
  const [description, setDescription] = useState('');
  const [entities, setEntities] = useState<EntityType[]>([]);
  const [relations, setRelations] = useState<RelationType[]>([]);
  const [expandedEntities, setExpandedEntities] = useState<Set<string>>(new Set());
  const [expandedRelations, setExpandedRelations] = useState<Set<string>>(new Set());
  const [selectedTab, setSelectedTab] = useState(0);
  const [saving, setSaving] = useState(false);

  // Parse initial YAML content
  useEffect(() => {
    if (initialContent) {
      try {
        const parsed = yaml.load(initialContent) as OntologySchemaData | { entity_types?: Array<EntityType & { name: string }>; relation_types?: Array<RelationType & { name: string }>; description?: string };

        if (parsed.description) {
          setDescription(parsed.description);
        }

        if (parsed.entity_types) {
          let entityArray: EntityType[] = [];

          // Handle both formats: object (key-value) and array (with name property)
          if (Array.isArray(parsed.entity_types)) {
            // Array format: [{ name: "X", parent: null, ... }, ...]
            entityArray = parsed.entity_types.map((entity) => ({
              id: `entity-${Math.random().toString(36).substr(2, 9)}`,
              name: entity.name || '',
              parent: entity.parent || null,
              description: entity.description || '',
              synonymes: entity.synonymes || [],
              proprietes: entity.proprietes || [],
              examples: entity.examples || [],
            }));
          } else {
            // Object format: { "EntityName": { parent: null, ... }, ... }
            entityArray = Object.entries(parsed.entity_types).map(([name, data]) => ({
              id: `entity-${Math.random().toString(36).substr(2, 9)}`,
              name,
              parent: data.parent || null,
              description: data.description || '',
              synonymes: data.synonymes || [],
              proprietes: data.proprietes || [],
              examples: data.examples || [],
            }));
          }

          setEntities(entityArray);
          // Cards closed by default
          setExpandedEntities(new Set());
        }

        if (parsed.relation_types) {
          let relationArray: RelationType[] = [];

          // Handle both formats: object and array
          if (Array.isArray(parsed.relation_types)) {
            // Array format: [{ name: "X", description: "...", source: "...", cible: "..." }, ...]
            relationArray = parsed.relation_types.map((relation) => ({
              id: `relation-${Math.random().toString(36).substr(2, 9)}`,
              name: relation.name || '',
              description: relation.description || '',
              source: relation.source || '',
              cible: relation.cible || '',
            }));
          } else {
            // Object format: { "relationName": { description: "...", source: "...", cible: "..." }, ... }
            relationArray = Object.entries(parsed.relation_types).map(([name, data]) => ({
              id: `relation-${Math.random().toString(36).substr(2, 9)}`,
              name,
              description: data.description || '',
              source: data.source || '',
              cible: data.cible || '',
            }));
          }

          setRelations(relationArray);
          // Cards closed by default
          setExpandedRelations(new Set());
        }
      } catch (error) {
        console.error('Failed to parse YAML:', error);
      }
    }
  }, [initialContent]);

  const handleAddEntity = () => {
    const newEntity: EntityType = {
      id: `entity-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      name: '',
      parent: null,
      description: '',
      synonymes: [],
      proprietes: [],
      examples: [],
    };
    // Add at the beginning
    setEntities([newEntity, ...entities]);
    setExpandedEntities(new Set([...expandedEntities, newEntity.id]));
  };

  const handleAddRelation = () => {
    const newRelation: RelationType = {
      id: `relation-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      name: '',
      description: '',
      source: '',
      cible: '',
    };
    // Add at the beginning
    setRelations([newRelation, ...relations]);
    setExpandedRelations(new Set([...expandedRelations, newRelation.id]));
  };

  const handleRemoveEntity = (id: string) => {
    setEntities(entities.filter(e => e.id !== id));
    const newExpanded = new Set(expandedEntities);
    newExpanded.delete(id);
    setExpandedEntities(newExpanded);
  };

  const handleRemoveRelation = (id: string) => {
    setRelations(relations.filter(r => r.id !== id));
    const newExpanded = new Set(expandedRelations);
    newExpanded.delete(id);
    setExpandedRelations(newExpanded);
  };

  const handleUpdateEntity = (id: string, field: keyof EntityType, value: unknown) => {
    const updated = entities.map(entity =>
      entity.id === id ? { ...entity, [field]: value } : entity
    );
    setEntities(updated);
  };

  const handleUpdateRelation = (id: string, field: keyof RelationType, value: unknown) => {
    const updated = relations.map(relation =>
      relation.id === id ? { ...relation, [field]: value } : relation
    );
    setRelations(updated);
  };

  const handleAddArrayItem = (entityId: string, field: 'synonymes' | 'proprietes' | 'examples') => {
    const updated = entities.map(entity => {
      if (entity.id === entityId) {
        return {
          ...entity,
          [field]: [...entity[field], ''],
        };
      }
      return entity;
    });
    setEntities(updated);
  };

  const handleUpdateArrayItem = (
    entityId: string,
    field: 'synonymes' | 'proprietes' | 'examples',
    itemIndex: number,
    value: string
  ) => {
    const updated = entities.map(entity => {
      if (entity.id === entityId) {
        const newArray = [...entity[field]];
        newArray[itemIndex] = value;
        return { ...entity, [field]: newArray };
      }
      return entity;
    });
    setEntities(updated);
  };

  const handleRemoveArrayItem = (
    entityId: string,
    field: 'synonymes' | 'proprietes' | 'examples',
    itemIndex: number
  ) => {
    const updated = entities.map(entity => {
      if (entity.id === entityId) {
        return {
          ...entity,
          [field]: entity[field].filter((_, idx) => idx !== itemIndex),
        };
      }
      return entity;
    });
    setEntities(updated);
  };

  const toggleEntityExpanded = (id: string) => {
    const newExpanded = new Set(expandedEntities);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedEntities(newExpanded);
  };

  const toggleRelationExpanded = (id: string) => {
    const newExpanded = new Set(expandedRelations);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRelations(newExpanded);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Convert to array format (backend expects this)
      const entityTypesArray = entities
        .filter(entity => entity.name.trim())
        .map(entity => ({
          name: entity.name,
          parent: entity.parent || null,
          description: entity.description,
          synonymes: entity.synonymes.filter(s => s.trim() !== ''),
          proprietes: entity.proprietes.filter(p => p.trim() !== ''),
          examples: entity.examples.filter(e => e.trim() !== ''),
        }));

      const relationTypesArray = relations
        .filter(relation => relation.name.trim())
        .map(relation => ({
          name: relation.name,
          description: relation.description,
          source: relation.source,
          cible: relation.cible,
        }));

      const data = {
        description,
        entity_types: entityTypesArray,
        relation_types: relationTypesArray,
      };

      const yamlContent = yaml.dump(data, {
        indent: 2,
        lineWidth: -1,
        noRefs: true,
      });

      await onSave(yamlContent);
    } catch (error) {
      console.error('Failed to save:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-h-[70vh] overflow-y-auto px-1">
      {/* Description globale */}
      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          {t('admin.configForms.ontologySchema.schemaDescription')}
        </label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={t('admin.configForms.ontologySchema.schemaDescriptionPlaceholder')}
          className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
          rows={3}
        />
      </div>

      {/* Tabs for Entity Types and Relation Types */}
      <Tab.Group selectedIndex={selectedTab} onChange={setSelectedTab}>
        <Tab.List className="flex gap-2 rounded-xl bg-muted p-1 mb-4">
          <Tab
            className={({ selected }) =>
              `flex-1 rounded-lg px-4 py-2.5 text-sm font-medium transition-colors flex items-center justify-center gap-2
              ${selected ? 'bg-background text-foreground shadow' : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground'}`
            }
          >
            <Box className="h-4 w-4" />
            {t('admin.configForms.ontologySchema.entityTypesTab', { total: entities.length })}
          </Tab>
          <Tab
            className={({ selected }) =>
              `flex-1 rounded-lg px-4 py-2.5 text-sm font-medium transition-colors flex items-center justify-center gap-2
              ${selected ? 'bg-background text-foreground shadow' : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground'}`
            }
          >
            <ArrowLeftRight className="h-4 w-4" />
            {t('admin.configForms.ontologySchema.relationTypesTab', { total: relations.length })}
          </Tab>
        </Tab.List>

        <Tab.Panels>
          {/* Entity Types Panel */}
          <Tab.Panel>
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-foreground">{t('admin.configForms.ontologySchema.entityTypes')}</h3>
                <Button onClick={handleAddEntity} size="sm">
                  <Plus className="h-4 w-4 mr-1" />
                  {t('admin.configForms.ontologySchema.addEntity')}
                </Button>
              </div>

              <div className="space-y-3">
                {entities.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Info className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">{t('admin.configForms.ontologySchema.noEntities')}</p>
                  </div>
                ) : (
                  entities.map((entity) => {
                    const isExpanded = expandedEntities.has(entity.id);

                    return (
                      <Card key={entity.id} className="border-border">
                        <CardHeader className="pb-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 flex-1">
                              <button
                                onClick={() => toggleEntityExpanded(entity.id)}
                                className="p-1 hover:bg-muted rounded"
                              >
                                {isExpanded ? (
                                  <ChevronUp className="h-4 w-4" />
                                ) : (
                                  <ChevronDown className="h-4 w-4" />
                                )}
                              </button>
                              <input
                                type="text"
                                value={entity.name}
                                onChange={(e) => handleUpdateEntity(entity.id, 'name', e.target.value)}
                                placeholder={t('admin.configForms.ontologySchema.entityNamePlaceholder')}
                                className="flex-1 px-3 py-1.5 text-sm font-semibold bg-background border border-border rounded focus:ring-2 focus:ring-primary focus:border-transparent"
                              />
                            </div>
                            <Button
                              onClick={() => handleRemoveEntity(entity.id)}
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </CardHeader>

                        {isExpanded && (
                          <CardContent className="space-y-4 pt-0">
                            {/* Parent */}
                            <div>
                              <label className="block text-xs font-medium text-foreground/70 mb-1">
                                {t('admin.configForms.ontologySchema.parentEntity')}
                              </label>
                              <input
                                type="text"
                                value={entity.parent || ''}
                                onChange={(e) => handleUpdateEntity(entity.id, 'parent', e.target.value || null)}
                                placeholder={t('admin.configForms.ontologySchema.parentEntityPlaceholder')}
                                className="w-full px-3 py-1.5 text-sm bg-background border border-border rounded focus:ring-2 focus:ring-primary focus:border-transparent"
                              />
                            </div>

                            {/* Description */}
                            <div>
                              <label className="block text-xs font-medium text-foreground/70 mb-1">
                                {t('global.description')}
                              </label>
                              <textarea
                                value={entity.description}
                                onChange={(e) => handleUpdateEntity(entity.id, 'description', e.target.value)}
                                placeholder={t('admin.configForms.ontologySchema.entityDescriptionPlaceholder')}
                                className="w-full px-3 py-2 text-sm bg-background border border-border rounded focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                                rows={2}
                              />
                            </div>

                            {/* Synonymes */}
                            <div>
                              <label className="block text-xs font-medium text-foreground/70 mb-1">
                                {t('admin.configForms.shared.synonyms')}
                              </label>
                              <div className="space-y-2">
                                {entity.synonymes.map((syn, synIndex) => (
                                  <div key={synIndex} className="flex gap-2">
                                    <input
                                      type="text"
                                      value={syn}
                                      onChange={(e) => handleUpdateArrayItem(entity.id, 'synonymes', synIndex, e.target.value)}
                                      placeholder={t('admin.configForms.shared.synonymPlaceholder')}
                                      className="flex-1 px-3 py-1.5 text-sm bg-background border border-border rounded focus:ring-2 focus:ring-primary focus:border-transparent"
                                    />
                                    <Button
                                      onClick={() => handleRemoveArrayItem(entity.id, 'synonymes', synIndex)}
                                      variant="ghost"
                                      size="sm"
                                      className="text-red-600"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </div>
                                ))}
                                <Button
                                  onClick={() => handleAddArrayItem(entity.id, 'synonymes')}
                                  variant="outline"
                                  size="sm"
                                >
                                  <Plus className="h-3 w-3 mr-1" />
                                  {t('admin.configForms.shared.addSynonym')}
                                </Button>
                              </div>
                            </div>

                            {/* Propriétés */}
                            <div>
                              <label className="block text-xs font-medium text-foreground/70 mb-1">
                                {t('admin.configForms.ontologySchema.properties')}
                              </label>
                              <div className="space-y-2">
                                {entity.proprietes.map((prop, propIndex) => (
                                  <div key={propIndex} className="flex gap-2">
                                    <input
                                      type="text"
                                      value={prop}
                                      onChange={(e) => handleUpdateArrayItem(entity.id, 'proprietes', propIndex, e.target.value)}
                                      placeholder={t('admin.configForms.ontologySchema.propertyPlaceholder')}
                                      className="flex-1 px-3 py-1.5 text-sm bg-background border border-border rounded focus:ring-2 focus:ring-primary focus:border-transparent"
                                    />
                                    <Button
                                      onClick={() => handleRemoveArrayItem(entity.id, 'proprietes', propIndex)}
                                      variant="ghost"
                                      size="sm"
                                      className="text-red-600"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </div>
                                ))}
                                <Button
                                  onClick={() => handleAddArrayItem(entity.id, 'proprietes')}
                                  variant="outline"
                                  size="sm"
                                >
                                  <Plus className="h-3 w-3 mr-1" />
                                  {t('admin.configForms.ontologySchema.addProperty')}
                                </Button>
                              </div>
                            </div>

                            {/* Examples */}
                            <div>
                              <label className="block text-xs font-medium text-foreground/70 mb-1">
                                {t('admin.configForms.shared.examples')}
                              </label>
                              <div className="space-y-2">
                                {entity.examples.map((example, exampleIndex) => (
                                  <div key={exampleIndex} className="flex gap-2">
                                    <input
                                      type="text"
                                      value={example}
                                      onChange={(e) => handleUpdateArrayItem(entity.id, 'examples', exampleIndex, e.target.value)}
                                      placeholder={t('admin.configForms.shared.examplePlaceholder')}
                                      className="flex-1 px-3 py-1.5 text-sm bg-background border border-border rounded focus:ring-2 focus:ring-primary focus:border-transparent"
                                    />
                                    <Button
                                      onClick={() => handleRemoveArrayItem(entity.id, 'examples', exampleIndex)}
                                      variant="ghost"
                                      size="sm"
                                      className="text-red-600"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </div>
                                ))}
                                <Button
                                  onClick={() => handleAddArrayItem(entity.id, 'examples')}
                                  variant="outline"
                                  size="sm"
                                >
                                  <Plus className="h-3 w-3 mr-1" />
                                  {t('admin.configForms.shared.addExample')}
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        )}
                      </Card>
                    );
                  })
                )}
              </div>
            </div>
          </Tab.Panel>

          {/* Relation Types Panel */}
          <Tab.Panel>
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-foreground">{t('admin.configForms.ontologySchema.relationTypes')}</h3>
                <Button onClick={handleAddRelation} size="sm">
                  <Plus className="h-4 w-4 mr-1" />
                  {t('admin.configForms.ontologySchema.addRelation')}
                </Button>
              </div>

              <div className="space-y-3">
                {relations.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Info className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">{t('admin.configForms.ontologySchema.noRelations')}</p>
                  </div>
                ) : (
                  relations.map((relation) => {
                    const isExpanded = expandedRelations.has(relation.id);

                    return (
                      <Card key={relation.id} className="border-border">
                        <CardHeader className="pb-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 flex-1">
                              <button
                                onClick={() => toggleRelationExpanded(relation.id)}
                                className="p-1 hover:bg-muted rounded"
                              >
                                {isExpanded ? (
                                  <ChevronUp className="h-4 w-4" />
                                ) : (
                                  <ChevronDown className="h-4 w-4" />
                                )}
                              </button>
                              <input
                                type="text"
                                value={relation.name}
                                onChange={(e) => handleUpdateRelation(relation.id, 'name', e.target.value)}
                                placeholder={t('admin.configForms.ontologySchema.relationNamePlaceholder')}
                                className="flex-1 px-3 py-1.5 text-sm font-semibold bg-background border border-border rounded focus:ring-2 focus:ring-primary focus:border-transparent"
                              />
                            </div>
                            <Button
                              onClick={() => handleRemoveRelation(relation.id)}
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </CardHeader>

                        {isExpanded && (
                          <CardContent className="space-y-4 pt-0">
                            {/* Description */}
                            <div>
                              <label className="block text-xs font-medium text-foreground/70 mb-1">
                                {t('global.description')}
                              </label>
                              <textarea
                                value={relation.description}
                                onChange={(e) => handleUpdateRelation(relation.id, 'description', e.target.value)}
                                placeholder={t('admin.configForms.ontologySchema.relationDescriptionPlaceholder')}
                                className="w-full px-3 py-2 text-sm bg-background border border-border rounded focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                                rows={2}
                              />
                            </div>

                            {/* Source */}
                            <div>
                              <label className="block text-xs font-medium text-foreground/70 mb-1">
                                {t('admin.configForms.ontologySchema.sourceLabel')}
                              </label>
                              <input
                                type="text"
                                value={relation.source}
                                onChange={(e) => handleUpdateRelation(relation.id, 'source', e.target.value)}
                                placeholder={t('admin.configForms.ontologySchema.sourcePlaceholder')}
                                className="w-full px-3 py-1.5 text-sm bg-background border border-border rounded focus:ring-2 focus:ring-primary focus:border-transparent"
                              />
                            </div>

                            {/* Cible */}
                            <div>
                              <label className="block text-xs font-medium text-foreground/70 mb-1">
                                {t('admin.configForms.ontologySchema.targetLabel')}
                              </label>
                              <input
                                type="text"
                                value={relation.cible}
                                onChange={(e) => handleUpdateRelation(relation.id, 'cible', e.target.value)}
                                placeholder={t('admin.configForms.ontologySchema.targetPlaceholder')}
                                className="w-full px-3 py-1.5 text-sm bg-background border border-border rounded focus:ring-2 focus:ring-primary focus:border-transparent"
                              />
                            </div>

                            <div className="bg-blue-50 dark:bg-blue-950/20 rounded-lg p-3 text-xs text-blue-800 dark:text-blue-200">
                              <p>
                                <strong>{t('admin.configForms.shared.noteLabel')}</strong> {t('admin.configForms.ontologySchema.relationNote')}
                              </p>
                            </div>
                          </CardContent>
                        )}
                      </Card>
                    );
                  })
                )}
              </div>
            </div>
          </Tab.Panel>
        </Tab.Panels>
      </Tab.Group>

      {/* Actions */}
      <div className="flex gap-3 justify-end pt-4 border-t border-border sticky bottom-0 bg-background">
        <Button onClick={onCancel} variant="outline" disabled={saving}>
          {t('actions.cancel')}
        </Button>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? t('actions.saving') : t('actions.save')}
        </Button>
      </div>
    </div>
  );
}
